// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Events from "@bg/XDL/Icons/Events";
import { xBaseTheme } from "@bg/XDL/types";
import { xScreen } from "@bg/XDL/Utilities";
import getColor from "@insite/mobius/utilities/getColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import { css } from "styled-components";

const arrowLeft =
    "PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5QzgyMkYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cG9seWxpbmUgcG9pbnRzPSIxNSAxOCA5IDEyIDE1IDYiPjwvcG9seWxpbmU+PC9zdmc+";
const arrowRight =
    "PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5QzgyMkYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cG9seWxpbmUgcG9pbnRzPSI5IDE4IDE1IDEyIDkgNiI+PC9wb2x5bGluZT48L3N2Zz4=";

export default {
    defaultProps: {
        calendarIconProps: {
            src: Events,
            css: css`
                padding-right: 0 !important;
                ${xScreen("lte", "xs")} {
                    padding-left: 0 !important;
                }
                svg g {
                    fill: ${getColor("tertiary.main")};
                }
            `,
        },
        cssOverrides: {
            datePicker: css`
                .react-datetime-picker {
                    border-radius: 0 !important;
                    background: ${getColor("common.white")} !important;
                    height: 36px;
                }

                .react-datetime-picker:focus-within {
                    border-radius: 0px;
                    padding: 0 10px;
                }

                button:focus span {
                    outline-color: transparent;
                    outline-style: none;
                    outline-width: 0;
                }

                .react-calendar {
                    margin-top: 0;
                    0 0 15px 0 rgba(79,79,119,0.18);
                    padding: 25px 30px 35px;
                    @media (max-width: 768px) {
                        padding: 10px;
                    }
                    button:enabled:hover:not(:disabled), button:enabled:focus:not(:disabled) {
                        background-color: ${getColor("primary")}
                    }
                    &__navigation {
                        margin-bottom: 15px;
                        @media (max-width: 768px) {
                            margin-bottom: 10px;
                        }

                        &__label {
                            font-size: 15px;
                            line-height: 18px;
                        }

                        &__next-button {
                            background-image: url('${`data:image/svg+xml;base64,${arrowRight}`}');
                        }
                        &__prev-button {
                            background-image: url('${`data:image/svg+xml;base64,${arrowLeft}`}');
                        }

                        &__next-button,
                        &__prev-button {
                            background-size: 20px;
                            color: ${getColor("tertiary.main")};
                            background-color: ${props =>
                                safeColor(props.theme.colors.primary.main).rgb().alpha(0.25).string()};
                        }
                    }

                    &__month-view__weekdays {
                        margin-bottom: 5px;

                        abbr {
                            font-size: 15px;
                            font-weight: 500;
                        }
                    }

                    &__month-view__days__day {
                        font-size: 15px;
                        border-radius: 0;
                        padding: 7px;
                        @media (max-width: 768px) {
                            padding: 5px;
                        }
                    }

                    &__tile--now {
                        font-weight: normal;
                        color: black;
                        position: relative;
                    }

                    button.react-calendar__tile.react-calendar__tile--now.react-calendar__month-view__days__day:after {
                        content: " ";
                        height: 4px;
                        width: 4px;
                        background: ${getColor("tertiary.main")};
                        position: absolute;
                        border-radius: 50%;
                        bottom: 0;
                        left: 50%;
                        transform: translateX(-50%);
                    }
                }
                .react-datetime-picker__inputGroup__divider {
                    color: ${getColor("text.main")};
                    opacity: 1;
                }
                .react-datetime-picker__inputGroup__input.react-datetime-picker__inputGroup__month[value=""] ~ .react-datetime-picker__inputGroup__divider {
                    opacity: 0.5;
                }

                .react-datetime-picker__wrapper {
                    display: flex;
                    align-items: center;

                    .react-datetime-picker__calendar-button.react-datetime-picker__button {
                        display: flex;
                        align-items: center;
                    }
                }
            `,
            formInputWrapper: css`
                .react-datetime-picker.react-datetime-picker--open.react-datetime-picker--enabled
                    .react-datetime-picker__calendar-button.react-datetime-picker__button:last-child {
                    span svg g {
                        fill: ${getColor("primary.main")};
                    }
                }
            `,
        },
    },
} as xBaseTheme["datePicker"];
